import { useAuth } from "@/contexts/AuthContext";
import { usePasscodeStatus } from "@/hooks/usePasscode";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useMigrationRequest } from "@/hooks/useMigration";
import { PasscodeSetupBanner } from "@/components/PasscodeSetupBanner";
import { MigrationBanner } from "@/components/migration/MigrationBanner";

/**
 * Dashboard banner manager: enforces priority hierarchy:
 * P1: Passcode setup (admins/super_admins only, non-dismissable)
 * P2: Migration promotion (after passcode resolved)
 * P3: School holiday, handled separately (operational, always shows)
 * P4: Onboarding hints, handled by FirstTimeHint
 */
export function DashboardBanners() {
  const { role } = useAuth();
  const { data: passcodeStatus, isLoading: loadingPasscode } = usePasscodeStatus();
  const { data: prefs, isLoading: loadingPrefs } = useUserPreferences();
  const { data: migrationRequest } = useMigrationRequest();

  const isAdminOrAbove = role === "super_admin" || role === "admin";

  // Wait until we know whether the higher-priority passcode nudge applies,
  // otherwise a lower-priority banner can flash for a frame before P1 takes over.
  if (isAdminOrAbove && (loadingPasscode || loadingPrefs)) {
    return null;
  }

  // P1: Passcode not set for admin/super_admin
  const showPasscodeNudge =
    isAdminOrAbove && passcodeStatus && !passcodeStatus.hasPasscode;

  if (showPasscodeNudge) {
    return <PasscodeSetupBanner />;
  }

  // P2: Migration banner, only if passcode is resolved (or user is not admin)
  const migrationDismissed = prefs?.migration_banner_dismissed === true;
  const hasMigrationRequest = !!migrationRequest;

  // Show migration banner if: not dismissed AND (no request yet, or request in progress/completed)
  if (!loadingPrefs && (!migrationDismissed || hasMigrationRequest)) {
    return <MigrationBanner />;
  }

  return null;
}

/**
 * Whether the dashboard onboarding hint should be suppressed
 * because a higher-priority banner is active.
 */
export function useShouldSuppressDashboardHint(): boolean {
  const { role } = useAuth();
  const { data: passcodeStatus, isLoading: loadingPasscode } = usePasscodeStatus();
  const { data: prefs, isLoading: loadingPrefs } = useUserPreferences();
  const { data: migrationRequest } = useMigrationRequest();

  const isAdminOrAbove = role === "super_admin" || role === "admin";

  if (loadingPasscode || loadingPrefs) return true; // suppress while loading

  // P1 active?
  if (isAdminOrAbove && passcodeStatus && !passcodeStatus.hasPasscode) return true;

  // P2 active?
  const migrationDismissed = prefs?.migration_banner_dismissed === true;
  if (!migrationDismissed || !!migrationRequest) return true;

  return false;
}
